"use client"

import { Button } from "@/components/ui/button"
import { createUser } from "@/lib/api/users"
import { isUserLoggedIn } from "@/lib/utils/user"
import { useLogin, usePrivy, User, useUser } from "@privy-io/react-auth"
import { useRouter, useSearchParams } from "next/navigation"
import { Suspense, useCallback, useEffect, useState } from "react"

type Props = {
  size?: "default" | "sm" | "lg" | "icon" | null | undefined
  variant?:
    | "default"
    | "destructive"
    | "outline"
    | "secondary"
    | "ghost"
    | "link"
    | null
    | undefined
  title?: string
  rightIcon?: React.ReactNode
  className?: string
  redirectNewUserToOnboarding?: boolean
}

// Prevent multiple instances of the LoginButton from handling the callback
const isBeingHandled = { current: false }

export default function LoginButtonWithSuspense(props: Props) {
  return (
    <Suspense>
      <LoginButton {...props} />
    </Suspense>
  )
}

function LoginButton({
  size,
  variant,
  title,
  rightIcon,
  className,
  redirectNewUserToOnboarding,
}: Props) {
  const [isLoggedIn, setisLoggedIn] = useState(false)
  const { ready, authenticated } = usePrivy()
  const { refreshUser } = useUser()

  const router = useRouter()
  const searchParams = useSearchParams()

  // Optional query param to trigger login modal on page load
  const openLoginModalOnLoad = searchParams?.get("login") === "true"
  // Optional query param with path to redirect to after login
  const redirect = searchParams?.get("redirect")

  useEffect(() => {
    const checkIsUserLoggedIn = async () => {
      const loggedIn = await isUserLoggedIn()
      setisLoggedIn(loggedIn)
    }
    checkIsUserLoggedIn()
  }, [])

  const onComplete = useCallback(
    async ({ user, isNewUser }: { user: User; isNewUser: boolean }) => {
      if (isBeingHandled.current) return

      isBeingHandled.current = true

      if (isNewUser) {
        // Create user record linked to Privy ID, add userId to Privy user custom metadata
        await createUser()

        // Refresh privy-id-token to contain updated custom metadata
        await refreshUser()

        // Route to onboarding if flag set (this is set for writer register flows)
        if (redirectNewUserToOnboarding) {
          router.push("/setup")
          return
        }
      }

      // If redirect supplied, redirect to the redirect path
      if (redirect) {
        router.push(redirect)
        return
      }

      // If logging in on the landing page, redirect to home
      if (window.location.pathname === "/") {
        router.push("/home")
      } // Otherwise, reload page to ensure server components pick up login state
      else {
        router.refresh()
      }

      isBeingHandled.current = false
    },
    [redirect, router, refreshUser]
  )

  const { login } = useLogin({ onComplete })

  // Open login modal if the user is not logged in, login param is set, and Privy is ready
  // else, if login param is set but user logged in, redirect to dashboard
  useEffect(() => {
    if (openLoginModalOnLoad && ready) {
      if (isLoggedIn) router.push(redirect || "/dashboard")
      else login()
    }
  }, [openLoginModalOnLoad, ready, redirect])

  // Disable login when Privy is not ready or the user is already authenticated
  const disableLogin = !ready || (ready && authenticated) || isLoggedIn

  return (
    <Button
      size={size}
      variant={variant}
      onClick={login}
      disabled={disableLogin}
      className={className}
    >
      {title ? title : "Sign in"}
      {rightIcon ? rightIcon : null}
    </Button>
  )
}
